import * as React from "react"
import {
  PreferenceCenter,
  Scope,
} from "@ua/web-sdk/lib/remote-data/preference-center"

import { EmailPreferenceCenterEntry } from "../../bootstrap"
import ContactClient, { ApiMutation } from "../clients/ContactClient"
import {
  createContactSubscriptions,
  DeviceType,
  deviceTypeToScope,
  getTimestamp,
  mergeChannelListsWithContactLists,
} from "../utils"
import {
  Loading,
  SavePreferences,
  Subscriptions,
  UpdateSubscriptions,
} from "./types"
import { SubscriptionFormClient } from "../../SubscriptionForm/client"

export default function useContactSubscriptionManager(
  preferenceCenter: PreferenceCenter,
  contactId: string,
  channelId: string,
  deviceType: DeviceType,
  entry: EmailPreferenceCenterEntry
): [Loading, Subscriptions, UpdateSubscriptions, SavePreferences] {
  const [loading, setLoading] = React.useState(true)
  const client = React.useMemo(
    () => new ContactClient(contactId, entry.auth.secret),
    [contactId, entry.auth]
  )
  const subscriptionFormClient = React.useMemo(
    () => new SubscriptionFormClient(channelId, entry.auth.secret),
    [channelId, entry.auth]
  )
  const [subscriptions, setSubscriptions] = React.useState<Subscriptions>({})
  const updateSubscriptions: UpdateSubscriptions = React.useCallback(
    (update) => {
      setSubscriptions((prevState) => {
        const prevSubs = { ...prevState }
        const lists = prevSubs[update.scope] ?? {}
        lists[update.id] = update.subscribed
        prevSubs[update.scope] = lists
        return { ...prevSubs }
      })
    },
    [subscriptions]
  )
  const savePreferences = React.useCallback(async () => {
    const mutations: ApiMutation[] = []
    const timestamp = getTimestamp(Date.now())
    Object.entries(subscriptions).forEach(([scope, subs]) => {
      Object.entries(subs ?? {}).forEach(([listId, subscribe]) => {
        mutations.push({
          action: subscribe ? "subscribe" : "unsubscribe",
          list_id: listId,
          scope: scope as Scope,
          timestamp,
        })
      })
    })
    return client.updateLists(mutations)
  }, [subscriptions])

  React.useEffect(() => {
    loadData()
  }, [])

  return [loading, subscriptions, updateSubscriptions, savePreferences]

  async function loadData(): Promise<void> {
    const scope = deviceTypeToScope(deviceType)
    let subscribedLists = await client.getLists()

    if (preferenceCenter.options?.merge_channel_data_to_contact) {
      const channelLevelLists = await subscriptionFormClient.getLists()

      subscribedLists = mergeChannelListsWithContactLists(scope, subscribedLists, channelLevelLists)
    }

    setSubscriptions(
      createContactSubscriptions(preferenceCenter, subscribedLists)
    )
    setLoading(false)
  }
}
